import { useState } from 'react'
import { type Meeting, type MeetingDraft, MeetingFields } from '@/entities/meeting'
import { dateTimeInputToIso, type FileRef, toDateTimeInput } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Dialog } from '@/shared/ui/dialog'
import { toast } from '@/shared/ui/toast'
import { useEditMeeting } from '../api/edit'

interface EditMeetingDialogProps {
  meeting: Meeting
  open: boolean
  onClose: () => void
}

function draftOf(m: Meeting): MeetingDraft {
  return {
    title: m.title,
    when: toDateTimeInput(m.when),
    location: m.location ?? '',
    participants: m.participants ?? '',
    url: m.url ?? '',
    notes: m.notes ?? '',
    customer_visible: m.customer_visible,
    attachments: m.attachments,
  }
}

export function EditMeetingDialog({ meeting, open, onClose }: EditMeetingDialogProps) {
  const edit = useEditMeeting(meeting.id)
  const [draft, setDraft] = useState<MeetingDraft>(() => draftOf(meeting))
  const [error, setError] = useState('')

  function submit() {
    if (!draft.title.trim()) return setError('Укажите тему')
    const when = dateTimeInputToIso(draft.when)
    if (!when) return setError('Укажите дату и время')
    setError('')
    const kept = new Set(draft.attachments.map((f) => f.key))
    const had = new Set(meeting.attachments.map((f) => f.key))
    const add: FileRef[] = draft.attachments.filter((f) => !had.has(f.key))
    const remove: FileRef[] = meeting.attachments.filter((f) => !kept.has(f.key))
    edit.mutate(
      {
        details: {
          title: draft.title.trim(),
          when,
          location: draft.location.trim(),
          participants: draft.participants.trim(),
          url: draft.url.trim(),
          notes: draft.notes.trim(),
          customer_visible: draft.customer_visible,
        },
        ...(add.length ? { add_attachments: add } : {}),
        ...(remove.length ? { remove_attachments: remove } : {}),
      },
      {
        onSuccess: () => {
          toast.success('Сохранено')
          onClose()
        },
        onError: () => toast.error('Не удалось сохранить'),
      },
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Встреча"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={edit.isPending}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={edit.isPending}>
            {edit.isPending ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <MeetingFields value={draft} onChange={setDraft} {...(error ? { error } : {})} />
    </Dialog>
  )
}
